import { Box, Button, Divider, Grid, Typography } from "@mui/material";
import { Screen } from "../../../main/components/screen/Screen";
import { useContext, useEffect, useState } from "react";
import { sqlQueriesApi } from "../../../services/api";
import Loading from "../../../main/components/Loading";
import EmptyStateNotFound from "../../clients/components/EmptyState";
import { MainContext } from "../../../main/context/MainContextProvider";
import { GetSqlQueriesResponse } from "../../../services/api/routes/sqlQueries";
import { QueryType } from "../types";
import RopTableSearch from "../../../main/components/RopTableSearch";
import { DatePicker } from "@mui/x-date-pickers";
import DatePickerLocalizationAdapter from "../../../main/context/DatePickerLocalizationAdapter";
import dayjs from "dayjs";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { IdbStorage } from "../db/queriesIdb";
import SqlQueryItem from "../components/SqlQueryItem";
import { handleUpdateStorage, replaceQueriesById } from "../helpers";

export default function SqlQueriesList() {
  const idbStorage = new IdbStorage();

  const { selectedDashboardUsina: selectedUsina, setToastNotifications } = useContext(MainContext);

  const [loading, setLoading] = useState(false);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [loadingRelatorioDiario, setLoadingRelatorioDiario] = useState(false);
  const [loadingRelatorioMensal, setLoadingRelatorioMensal] = useState(false);

  const [sqlQueries, setSqlQueries] = useState<GetSqlQueriesResponse>();
  const [filteredSqlQueries, setFilteredSqlQueries] =
    useState<GetSqlQueriesResponse>();
  const [sqlQueriesOpened, setSqlQueriesOpened] = useState<string[]>([""]);
  const [search, setSearch] = useState("");
  const [reportMonthandYear, setReportMonthandYear] = useState<{
    month: number;
    year: number;
  }>({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  });
  const reportMonthDate = dayjs(
    new Date(reportMonthandYear.year, reportMonthandYear.month)
  );
  const formattedMonthlyReportDate = dayjs(
    new Date(reportMonthDate.year(), reportMonthDate.month(), 1)
  ).format("YYYY-MM-DD");

  const [reportDate, setReportDate] = useState<string>(
    dayjs(new Date()).format("YYYY-MM-DD")
  );

  const [dashboardDate, setDashboardDate] = useState<string>(
    dayjs(new Date()).format("YYYY-MM-DD")
  );

  const [expandDashboard, setExpandDashboard] = useState(true);
  const [expandRelatorioDiario, setExpandRelatorioDiario] = useState(true);
  const [expandRelatorioMensal, setExpandRelatorioMensal] = useState(true);

  const dbKeys = {
    dashboard:
      "sqlQueries-" +
      selectedUsina?.usinaIds.ropInputId +
      "-dashboard-" +
      dashboardDate,
    dailyReport:
      "sqlQueries-" +
      selectedUsina?.usinaIds.ropInputId +
      "-report-" +
      reportDate,
    monthlyReport:
      "sqlQueries-" +
      selectedUsina?.usinaIds.ropInputId +
      "-monthlyReport-" +
      formattedMonthlyReportDate,
  };

  const reportDayJsDate = reportDate ? dayjs(reportDate) : null;

  const dashboardDayJsDate = dashboardDate ? dayjs(dashboardDate) : null;

  const toggleSqlQuery = (queryId: string) => {
    if (sqlQueriesOpened.includes(queryId)) {
      const newArray = sqlQueriesOpened.filter((item) => item !== queryId);
      setSqlQueriesOpened(newArray);
    } else {
      setSqlQueriesOpened([...sqlQueriesOpened, queryId]);
    }
  };

  async function updateList() {
    await updateAllQueries();
  }
  async function updateQuery(
    queryType: QueryType,
    setQueryLoading?: (value: boolean) => void
  ) {
    setQueryLoading && setQueryLoading(true);
    try {
      let dbKey = "";
      switch (queryType) {
        case "dashboardQueries":
          dbKey = dbKeys.dashboard;
          break;
        case "relatorioDiarioQueries":
          dbKey = dbKeys.dailyReport;
          break;
        case "relatorioMensalQueries":
          dbKey = dbKeys.monthlyReport;
          break;
      }
      const queriesFromStorage = await idbStorage.getItem(dbKey);
      const sqlQueriesResponse = await sqlQueriesApi.getSqlQueries();

      const queries = sqlQueriesResponse?.data;
      const parsedQueries = queriesFromStorage
        ? JSON.parse(queriesFromStorage)[queryType]
        : null;

      if (parsedQueries) {
        queries[queryType] = replaceQueriesById(
          queries[queryType],
          parsedQueries
        );
      }
      setSqlQueries((prevQueries) => {
        const updatedQueries = {
          ...prevQueries,
          relatorioMensalQueries: prevQueries?.relatorioMensalQueries || [],
          relatorioDiarioQueries: prevQueries?.relatorioDiarioQueries || [],
          dashboardQueries: prevQueries?.dashboardQueries || [],
        };

        if (queryType === "relatorioMensalQueries") {
          updatedQueries.relatorioMensalQueries = queries[queryType];
        } else if (queryType === "relatorioDiarioQueries") {
          updatedQueries.relatorioDiarioQueries = queries[queryType];
        } else if (queryType === "dashboardQueries") {
          updatedQueries.dashboardQueries = queries[queryType];
        }

        return updatedQueries;
      });

      setFilteredSqlQueries((prevQueries) => {
        const updatedQueries = {
          ...prevQueries,
          relatorioMensalQueries: prevQueries?.relatorioMensalQueries || [],
          relatorioDiarioQueries: prevQueries?.relatorioDiarioQueries || [],
          dashboardQueries: prevQueries?.dashboardQueries || [],
        };
        if (queryType === "relatorioMensalQueries") {
          updatedQueries.relatorioMensalQueries = queries[queryType];
        } else if (queryType === "relatorioDiarioQueries") {
          updatedQueries.relatorioDiarioQueries = queries[queryType];
        } else if (queryType === "dashboardQueries") {
          updatedQueries.dashboardQueries = queries[queryType];
        }

        return updatedQueries;
      });
    } catch (error) {
      if (error instanceof Error) {
        showError(error);
      }
    } finally {
      setQueryLoading && setQueryLoading(false);
    }
  }

  async function updateAllQueries() {
    setLoading(true);
    try {
      await Promise.all([
        updateQuery("dashboardQueries", setLoadingDashboard),
        updateQuery("relatorioDiarioQueries", setLoadingRelatorioDiario),
        updateQuery("relatorioMensalQueries", setLoadingRelatorioMensal),
      ]);
      showSuccess("Consultas atualizadas com sucesso");
    } catch (error) {
      if (error instanceof Error) {
        showError(error);
      }
    } finally {
      setLoading(false);
    }
  }
  async function updateDashboardQueries() {
    await updateQuery("dashboardQueries", setLoadingDashboard);
  }
  async function updateDailyReportQueries() {
    await updateQuery("relatorioDiarioQueries", setLoadingRelatorioDiario);
  }
  async function updateMonthlyReportQueries() {
    await updateQuery("relatorioMensalQueries", setLoadingRelatorioMensal);
  }
  useEffect(() => {
    updateList();
  }, [selectedUsina]);

  useEffect(() => {
    updateDashboardQueries();
  }, [dashboardDate]);

  useEffect(() => {
    updateDailyReportQueries();
  }, [reportDate]);

  useEffect(() => {
    updateMonthlyReportQueries();
  }, [formattedMonthlyReportDate]);

  useEffect(() => {
    const filteredDashboardQueries =
      sqlQueries?.dashboardQueries.filter((query) =>
        query.name.toLowerCase().includes(search.toLowerCase())
      ) || [];
    const filteredRelatorioDiarioQueries =
      sqlQueries?.relatorioDiarioQueries.filter((query) =>
        query.name.toLowerCase().includes(search.toLowerCase())
      ) || [];
    const filteredRelatorioMensalQueries =
      sqlQueries?.relatorioMensalQueries.filter((query) =>
        query.name.toLowerCase().includes(search.toLowerCase())
      ) || [];
    setFilteredSqlQueries({
      dashboardQueries: filteredDashboardQueries,
      relatorioDiarioQueries: filteredRelatorioDiarioQueries,
      relatorioMensalQueries: filteredRelatorioMensalQueries,
    });
  }, [search]);

  const executeGroupQueries = async (queryType: QueryType) => {
    const loading = getSetLoadingByQueryType(queryType);
    try {
      loading(true);
      if (!selectedUsina) {
        throw new Error(
          "É necessário selecionar uma usina para executar as consultas"
        );
      }
      if (!sqlQueries) {
        throw new Error(
          "Não foi possível executar as consultas, consultas não encontradas"
        );
      }
      await Promise.all(
        sqlQueries[queryType].map(async (query) => {
          await executeSqlQuery(query.id, selectedUsina?.usinaIds.ropInputId);
        })
      );
      showSuccess("Consultas executadas com sucesso");
    } catch (error) {
      if (error instanceof Error) {
        showError(error);
      }
    } finally {
      loading(false);
    }
  };
  const showError = (error: Error) => {
    setToastNotifications({
      isAdding: true,
      notifications: [
        {
          text: error.message,
          severity: "warning",
        },
      ],
    });
  };

  const showSuccess = (message: string) => {
    setToastNotifications({
      isAdding: true,
      notifications: [
        {
          text: message,
          severity: "success",
        },
      ],
    });
  };
  const executeSqlQuery = async (
    queryId: string,
    usinaId: number,
    setQueryLoading?: (loading: boolean) => void
  ) => {
    setQueryLoading && setQueryLoading(true);
    try {
      const dbKey = getDbKeyByQueryId(queryId);
      const queryType = getQueryType(queryId);

      if (!queryType) {
        throw new Error("Tipo de query inválido");
      }
      var referenceDate = "";
      switch (queryType) {
        case "dashboardQueries":
          if (!dashboardDate) {
            throw new Error("Data de referência inválida");
          }
          referenceDate = dashboardDate;
          break;
        case "relatorioDiarioQueries":
          if (!reportDate) {
            throw new Error("Data de referência inválida");
          }
          referenceDate = reportDate;
          break;
        case "relatorioMensalQueries":
          if (!formattedMonthlyReportDate) {
            throw new Error("Data de referência inválida");
          }
          referenceDate = formattedMonthlyReportDate;
          break;
      }

      var updatedQueryResponse = await sqlQueriesApi.executeSqlQuery({
        queryId,
        referenceDate,
        usinaId,
      });
      var updatedQuery = updatedQueryResponse.data.executedQuery;
      if (!selectedUsina) {
        throw new Error(
          "É necessário selecionar uma usina para executar as consultas"
        );
      }
      if (!sqlQueries) {
        throw new Error(
          "Não foi possível executar as consultas, consultas não encontradas"
        );
      }
      await handleUpdateStorage(updatedQuery, sqlQueries, idbStorage, dbKey);
      await updateQuery(queryType, setQueryLoading);
    } catch (error) {
      if (error instanceof Error) {
        showError(error);
      }
    } finally {
      setQueryLoading && setQueryLoading(false);
    }
  };

  const executeQuery = async (
    queryId: string,
    setLoading: (loading: boolean) => void
  ) => {
    try {
      if (!selectedUsina) {
        throw new Error("É necessário selecionar uma usina");
      }
      const type = getQueryType(queryId);
      if (!type) {
        throw new Error("Tipo de query inválido");
      }

      await executeSqlQuery(
        queryId,
        selectedUsina?.usinaIds.ropInputId,
        setLoading
      );
      showSuccess("Consulta executada com sucesso");
    } catch (error) {
      if (error instanceof Error) {
        showError(error);
      }
    }
  };

  const clearUsinaQueries = async () => {
    try {
      setLoading(true);
      await idbStorage.clearStore();
      await updateAllQueries();
      showSuccess("Dados de análise limpos com sucesso");
    } catch (error) {
      if (error instanceof Error) {
        showError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const today = dayjs(new Date());

  const getQueryType = (queryId: string): QueryType | null => {
    if (
      sqlQueries?.dashboardQueries.map((query) => query.id).includes(queryId)
    ) {
      return "dashboardQueries";
    }
    if (
      sqlQueries?.relatorioDiarioQueries
        .map((query) => query.id)
        .includes(queryId)
    ) {
      return "relatorioDiarioQueries";
    }
    if (
      sqlQueries?.relatorioMensalQueries
        .map((query) => query.id)
        .includes(queryId)
    ) {
      return "relatorioMensalQueries";
    }
    return null;
  };

  const getSetLoadingByQueryType = (queryType: QueryType) => {
    switch (queryType) {
      case "dashboardQueries":
        return setLoadingDashboard;
      case "relatorioDiarioQueries":
        return setLoadingRelatorioDiario;
      case "relatorioMensalQueries":
        return setLoadingRelatorioMensal;
    }
  };
  const getDbKeyByQueryId = (queryId: string): string => {
    const queryType = getQueryType(queryId);
    if (!queryType) {
      throw new Error("Tipo de query inválido");
    }
    var dbKey = "";
    switch (queryType) {
      case "dashboardQueries":
        dbKey = dbKeys.dashboard;
        break;
      case "relatorioDiarioQueries":
        dbKey = dbKeys.dailyReport;
        break;
      case "relatorioMensalQueries":
        dbKey = dbKeys.monthlyReport;
        break;
    }
    return dbKey;
  };
  const clearQueryData = async (queryId: string) => {
    new Promise<void>(async (resolve, reject) => {
      if (!selectedUsina) {
        reject("É necessário selecionar uma usina");
      }
      const dbKey = getDbKeyByQueryId(queryId);
      const localQueries = await idbStorage.getItem(dbKey);
      if (!localQueries) {
        reject("Queries não encontradas");
        return;
      }
      const parsedQueries: GetSqlQueriesResponse = JSON.parse(localQueries);
      if (!parsedQueries) {
        reject("Erro ao parsear queries");
        return;
      }
      const dashboardQueries = parsedQueries.dashboardQueries || [];
      const relatorioDiarioQueries = parsedQueries.relatorioDiarioQueries || [];
      const relatorioMensalQueries = parsedQueries.relatorioMensalQueries || [];
      const updatedDashboardQueries = dashboardQueries.map((query) => {
        if (query.id === queryId) {
          query.csvResult = "";
          query.elapsedExecutionTime = "00:00:00";
          query.error = null;
        }
        return query;
      });
      const updatedRelatorioDiarioQueries = relatorioDiarioQueries.map(
        (query) => {
          if (query.id === queryId) {
            query.csvResult = "";
            query.elapsedExecutionTime = "00:00:00";
            query.error = null;
          }
          return query;
        }
      );
      const updatedRelatorioMensalQueries = relatorioMensalQueries.map(
        (query) => {
          if (query.id === queryId) {
            query.csvResult = "";
            query.elapsedExecutionTime = "00:00:00";
            query.error = null;
          }
          return query;
        }
      );
      parsedQueries.dashboardQueries = updatedDashboardQueries;
      parsedQueries.relatorioDiarioQueries = updatedRelatorioDiarioQueries;
      parsedQueries.relatorioMensalQueries = updatedRelatorioMensalQueries;
      await idbStorage.setItem(dbKey, JSON.stringify(parsedQueries));
      updateList();
      resolve();
    });
  };

  return (
    <Screen.Root>
      {loading ? (
        <>
          <Screen.Container>
            <Loading />
          </Screen.Container>
        </>
      ) : (
        <>
          <Screen.Header hasUsinaSelect>
            <Typography
              variant="h4"
              sx={{
                fontWeight: "400",
                paddingTop: "15px",
                paddingBottom: "15px",
              }}
            >
              Análise de Consultas SQL
            </Typography>
          </Screen.Header>
          <Screen.Container>
            <Grid
              width={"100%"}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "start",
                justifyContent: "center",
                padding: "15px",
              }}
            >
              <Grid
                width={"100%"}
                display={"flex"}
                flexDirection={"row"}
                alignItems={"center"}
                justifyContent={"end"}
                marginBottom={"30px"}
                marginTop={"10px"}
              >
                <RopTableSearch search={search} setSearch={setSearch} />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={clearUsinaQueries}
                  sx={{
                    marginLeft: "10px",
                    marginRight: "40px",
                  }}
                >
                  Limpar Dados de Análise
                </Button>
              </Grid>

              <Grid display={"flex"} flexDirection={"row"} container>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  display={"flex"}
                  justifyContent={"start"}
                  alignItems={"center"}
                >
                  <Typography variant="h6">Consultas do Dashboard</Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"end"}
                  gap={2}
                >
                  <DatePickerLocalizationAdapter>
                    <DatePicker
                      format="DD/MM/YYYY"
                      value={dashboardDayJsDate}
                      onChange={(e) =>
                        e && setDashboardDate(e.format("YYYY-MM-DD"))
                      }
                      maxDate={today}
                      label={"Data Referência"}
                      sx={{
                        width: "100%",
                        maxWidth: "200px",
                        marginY: "12px",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "4px",
                          height: "40px",
                        },
                      }}
                    />
                  </DatePickerLocalizationAdapter>

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={async () => {
                      await executeGroupQueries("dashboardQueries");
                    }}
                    sx={{
                      marginLeft: "10px",
                      height: "40px",
                      minWidth: "100px",
                    }}
                  >
                    Executar Consultas
                  </Button>
                  <Grid
                    sx={{ justifyContent: "flex-end", display: "flex" }}
                    item
                  >
                    <KeyboardArrowRightIcon
                      sx={{
                        height: "24px",
                        width: "24px",
                        cursor: "pointer",
                        transform: expandDashboard
                          ? "rotate(90deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.1s ease-in-out",
                        color: "#FFF",
                      }}
                      onClick={() => setExpandDashboard(!expandDashboard)}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Divider
                sx={{
                  backgroundColor: "#FFFFFF",
                  width: "100%",
                  marginY: "10px",
                }}
              />
              {loadingDashboard ? (
                <Loading />
              ) : (
                <>
                  {expandDashboard && (
                    <>
                      {filteredSqlQueries &&
                        filteredSqlQueries.dashboardQueries.length > 0 ? (
                        <Box sx={{ width: "100%" }}>
                          {filteredSqlQueries.dashboardQueries?.map((query) => (
                            <SqlQueryItem
                              clearQueryData={clearQueryData}
                              key={query.id}
                              query={query}
                              toggleExpandQueryInfo={toggleSqlQuery}
                              isExpanded={sqlQueriesOpened.includes(query.id)}
                              executeQuery={executeQuery}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Screen.Container>
                          <EmptyStateNotFound title="Nenhum registro encontrado" />
                        </Screen.Container>
                      )}
                    </>
                  )}
                </>
              )}
            </Grid>

            <Grid
              width={"100%"}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "start",
                justifyContent: "center",
                padding: "15px",
              }}
            >
              <Grid display={"flex"} flexDirection={"row"} container>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  display={"flex"}
                  justifyContent={"start"}
                  alignItems={"center"}
                >
                  <Typography variant="h6">
                    Consultas do Relatório Diário
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"end"}
                  gap={2}
                >
                  <DatePickerLocalizationAdapter>
                    <DatePicker
                      format="DD/MM/YYYY"
                      value={reportDayJsDate}
                      onChange={(e) =>
                        e && setReportDate(e.format("YYYY-MM-DD"))
                      }
                      maxDate={today}
                      label={"Data Referência"}
                      sx={{
                        width: "100%",
                        maxWidth: "200px",
                        marginY: "12px",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "4px",
                          height: "40px",
                        },
                      }}
                    />
                  </DatePickerLocalizationAdapter>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={async () => {
                      await executeGroupQueries("relatorioDiarioQueries");
                    }}
                    sx={{
                      marginLeft: "10px",
                      height: "40px",
                      minWidth: "100px",
                    }}
                  >
                    Executar Consultas
                  </Button>
                  <Grid
                    sx={{ justifyContent: "flex-end", display: "flex" }}
                    item
                  >
                    <KeyboardArrowRightIcon
                      sx={{
                        height: "24px",
                        width: "24px",
                        cursor: "pointer",
                        transform: expandRelatorioDiario
                          ? "rotate(90deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.1s ease-in-out",
                        color: "#FFF",
                      }}
                      onClick={() =>
                        setExpandRelatorioDiario(!expandRelatorioDiario)
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Divider
                sx={{
                  backgroundColor: "#FFFFFF",
                  width: "100%",
                  marginY: "10px",
                }}
              />
              {loadingRelatorioDiario ? (
                <Loading />
              ) : (
                <>
                  {expandRelatorioDiario && (
                    <>
                      {filteredSqlQueries &&
                        filteredSqlQueries.relatorioDiarioQueries.length > 0 ? (
                        <Box sx={{ width: "100%" }}>
                          {filteredSqlQueries.relatorioDiarioQueries?.map(
                            (query) => (
                              <SqlQueryItem
                                clearQueryData={clearQueryData}
                                key={query.id}
                                query={query}
                                toggleExpandQueryInfo={toggleSqlQuery}
                                isExpanded={sqlQueriesOpened.includes(query.id)}
                                executeQuery={executeQuery}
                              />
                            )
                          )}
                        </Box>
                      ) : (
                        <Screen.Container>
                          <EmptyStateNotFound title="Nenhum registro encontrado" />
                        </Screen.Container>
                      )}
                    </>
                  )}
                </>
              )}
            </Grid>

            <Grid
              width={"100%"}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "start",
                justifyContent: "center",
                padding: "15px",
              }}
            >
              <Grid display={"flex"} flexDirection={"row"} container>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  display={"flex"}
                  justifyContent={"start"}
                  alignItems={"center"}
                >
                  <Typography variant="h6">
                    Consultas do Relatório Mensal
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"end"}
                  gap={2}
                >
                  <DatePickerLocalizationAdapter>
                    <DatePicker
                      format="MM/YYYY"
                      views={["month", "year"]}
                      value={reportMonthDate}
                      maxDate={today}
                      onChange={(date) => {
                        if (!date) return;
                        setReportMonthandYear({
                          month: date.month(),
                          year: date.year(),
                        });
                      }}
                      label={"Mês referência"}
                      sx={{
                        width: "100%",
                        maxWidth: "200px",
                        marginY: "12px",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "4px",
                          height: "40px",
                        },
                      }}
                    />
                  </DatePickerLocalizationAdapter>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={async () => {
                      await executeGroupQueries("relatorioMensalQueries");
                    }}
                    sx={{
                      marginLeft: "10px",
                      height: "40px",
                      minWidth: "100px",
                    }}
                  >
                    Executar Consultas
                  </Button>
                  <Grid
                    sx={{ justifyContent: "flex-end", display: "flex" }}
                    item
                  >
                    <KeyboardArrowRightIcon
                      sx={{
                        height: "24px",
                        width: "24px",
                        cursor: "pointer",
                        transform: expandRelatorioMensal
                          ? "rotate(90deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.1s ease-in-out",
                        color: "#FFF",
                      }}
                      onClick={() =>
                        setExpandRelatorioMensal(!expandRelatorioMensal)
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Divider
                sx={{
                  backgroundColor: "#FFFFFF",
                  width: "100%",
                  marginY: "10px",
                }}
              />
              {loadingRelatorioMensal ? (
                <Loading />
              ) : (
                <>
                  {expandRelatorioMensal && (
                    <>
                      {filteredSqlQueries &&
                        filteredSqlQueries.relatorioMensalQueries.length > 0 ? (
                        <Box sx={{ width: "100%" }}>
                          {filteredSqlQueries.relatorioMensalQueries?.map(
                            (query) => (
                              <SqlQueryItem
                                clearQueryData={clearQueryData}
                                key={query.id}
                                query={query}
                                toggleExpandQueryInfo={toggleSqlQuery}
                                isExpanded={sqlQueriesOpened.includes(query.id)}
                                executeQuery={executeQuery}
                              />
                            )
                          )}
                        </Box>
                      ) : (
                        <Screen.Container>
                          <EmptyStateNotFound title="Nenhum registro encontrado" />
                        </Screen.Container>
                      )}
                    </>
                  )}
                </>
              )}
            </Grid>
          </Screen.Container>
        </>
      )}
    </Screen.Root>
  );
}
